/* Banking Portal · Wave-12 — Trust Posture hero (Feb 2026).
   Sits at the top of /banking (Overview) and shows the sponsor bank
   exactly what controls Verqo runs, organized by NIST CSF, plus an
   honest gap-disclosure card.

   Key design rule (from product judgment):
   - Never claim "100% safe". The disclaimer is rendered prominently.
   - Evidence > marketing copy.
*/
import { useEffect, useState } from "react";
import api from "../../lib/api";
import {
  ShieldCheck, ShieldAlert, Eye, Lock, Activity, RotateCw,
  CheckCircle2, AlertTriangle, XCircle, ChevronRight, FileCheck2,
  Layers, Award, ScrollText, Network, Zap,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Link } from "react-router-dom";

function fmt(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US").format(n);
}
function pct(n) {
  if (n == null) return "—";
  return `${n}%`;
}

const STATUS_TONE = {
  operational:   { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  partial:       { cls: "bg-amber-50 text-amber-700 border-amber-200",       icon: AlertTriangle },
  missing:       { cls: "bg-rose-50 text-rose-700 border-rose-200",          icon: XCircle },
  gap:           { cls: "bg-rose-50 text-rose-700 border-rose-200",          icon: XCircle },
  informational: { cls: "bg-slate-50 text-slate-600 border-slate-200",       icon: Eye },
};

const CSF_META = {
  identify: { label: "Identify", icon: Eye,          color: "sky",
              blurb: "Who are our customers, vendors, and assets?" },
  protect:  { label: "Protect",  icon: ShieldCheck,  color: "emerald",
              blurb: "What stops bad things from happening?" },
  detect:   { label: "Detect",   icon: Activity,     color: "violet",
              blurb: "How fast do we know when something goes wrong?" },
  respond:  { label: "Respond",  icon: AlertTriangle, color: "amber",
              blurb: "What do we do when an incident occurs?" },
  recover:  { label: "Recover",  icon: RotateCw,     color: "slate",
              blurb: "How do we bounce back?" },
};

function HeroScorecard({ label, value, sub, tone = "slate", icon: Icon, link, testid }) {
  const cls = {
    emerald: "bg-emerald-50 border-emerald-200",
    amber:   "bg-amber-50 border-amber-200",
    rose:    "bg-rose-50 border-rose-200",
    sky:     "bg-sky-50 border-sky-200",
    violet:  "bg-violet-50 border-violet-200",
    slate:   "bg-slate-50 border-slate-200",
  }[tone] || "bg-slate-50 border-slate-200";

  const inner = (
    <div data-testid={testid} className={`rounded-xl border ${cls} px-4 py-3 h-full`}>
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] uppercase tracking-wider text-slate-500">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
      </div>
      <div className="text-2xl font-semibold tabular-nums text-slate-900 mt-1.5">{value}</div>
      {sub && <div className="text-[10.5px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
  return link ? <Link to={link} className="block hover:opacity-90 transition">{inner}</Link> : inner;
}

function CsfFunctionCard({ fnKey, body }) {
  const meta = CSF_META[fnKey];
  const Icon = meta.icon;
  const colorPct = body.coverage.coverage_pct >= 75 ? "emerald"
                   : body.coverage.coverage_pct >= 50 ? "amber" : "rose";
  return (
    <div data-testid={`csf-${fnKey}`} className="rounded-xl border border-slate-200 bg-white">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-slate-500" />
          <div>
            <div className="text-sm font-semibold text-slate-900">{meta.label}</div>
            <div className="text-[10.5px] text-slate-500">{meta.blurb}</div>
          </div>
        </div>
        <div className="text-right">
          <div className={`text-lg font-semibold tabular-nums ${
            colorPct === "emerald" ? "text-emerald-600"
            : colorPct === "amber"   ? "text-amber-600"
            : "text-rose-600"}`}>
            {body.coverage.coverage_pct}%
          </div>
          <div className="text-[10.5px] text-slate-500 tabular-nums">
            {body.coverage.operational}/{body.coverage.total} ops
          </div>
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {body.controls.map((c, i) => {
          const t = STATUS_TONE[c.status] || STATUS_TONE.informational;
          const StatusIcon = t.icon;
          return (
            <div key={i} data-testid={`csf-${fnKey}-ctrl-${i}`} className="px-4 py-2.5 flex items-start justify-between gap-3">
              <div className="flex items-start gap-2.5 min-w-0">
                <StatusIcon className={`w-3.5 h-3.5 mt-0.5 ${
                  c.tone === "emerald" ? "text-emerald-500"
                  : c.tone === "amber" ? "text-amber-500"
                  : c.tone === "rose"  ? "text-rose-500"
                  : "text-slate-400"}`} />
                <div className="min-w-0">
                  <div className="text-[12.5px] text-slate-900 font-medium">{c.name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 truncate">{c.signal}</div>
                </div>
              </div>
              {c.evidence ? (
                <Link to={c.evidence} className="text-[10.5px] text-sky-600 hover:underline flex items-center gap-0.5 shrink-0">
                  evidence <ChevronRight className="w-3 h-3" />
                </Link>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FrameworkPill({ framework }) {
  const cls = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber:   "bg-amber-50 text-amber-700 border-amber-200",
    rose:    "bg-rose-50 text-rose-700 border-rose-200",
    slate:   "bg-slate-50 text-slate-600 border-slate-200",
  }[framework.tone];
  return (
    <div data-testid={`framework-${framework.key}`} className={`rounded-lg border ${cls} px-3 py-2 text-left`}>
      <div className="text-[12px] font-semibold uppercase tracking-wide">{framework.label}</div>
      <div className="text-[10.5px] mt-0.5 capitalize">{(framework.status || "—").replace(/_/g, " ")}</div>
      <div className="text-[10px] opacity-75 mt-0.5">{framework.note}</div>
    </div>
  );
}

function GapCard({ gap, testid }) {
  const cls = {
    rose:  "bg-rose-50 border-rose-200 text-rose-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
  }[gap.tone] || "bg-slate-50 border-slate-200 text-slate-700";
  return (
    <div data-testid={testid} className={`rounded-lg border-2 ${cls} px-4 py-3`}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold">{gap.title}</div>
        <Badge variant="outline" className="text-[9.5px] uppercase">{gap.severity}</Badge>
      </div>
      <div className="text-[11.5px] mt-1.5 opacity-90">{gap.detail}</div>
      <div className="text-[11px] mt-2 opacity-80">
        <span className="font-medium">Remediation:</span> {gap.remediation}
      </div>
      <div className="text-[10.5px] mt-1 opacity-70">ETA: {gap.eta}</div>
    </div>
  );
}

export function TrustPostureHero() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/partner/banking/trust-posture/overview")
      .then(r => setData(r.data))
      .catch(e => setErr(e?.message || "Failed to load"));
  }, []);

  if (err) return null;
  if (!data) return <div className="text-sm text-slate-400">Loading trust posture…</div>;

  const trustTone = data.trust_score >= 75 ? "emerald"
                  : data.trust_score >= 50 ? "amber" : "rose";

  return (
    <div className="space-y-5" data-testid="trust-posture-hero">
      {/* Header */}
      <div className="rounded-2xl border-2 border-slate-200 bg-gradient-to-br from-slate-50 to-white p-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-slate-500" /> Trust Posture
              <Badge variant="outline" className="text-[10px] bg-slate-50">NIST CSF · live evidence</Badge>
            </h2>
            <p className="text-[12px] text-slate-500 mt-1 max-w-3xl">
              Every safety/security/compliance control we operate, organized in the framework your examiners use.
              <strong className="text-slate-700"> We do not claim 100% risk-free operation</strong> — known gaps are
              disclosed openly below so you can make informed risk decisions.
            </p>
          </div>
          <div className="text-right" data-testid="trust-score-badge">
            <div className={`text-4xl font-bold tabular-nums ${
              trustTone === "emerald" ? "text-emerald-600"
              : trustTone === "amber"   ? "text-amber-600"
              : "text-rose-600"}`}>{data.trust_score}</div>
            <div className="text-[10.5px] uppercase tracking-wider text-slate-500">control coverage</div>
            <div className="text-[10.5px] text-slate-500 tabular-nums mt-0.5">
              {data.control_totals.operational}/{data.control_totals.total} controls operational
            </div>
          </div>
        </div>

        {/* Hero scorecards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-5">
          <HeroScorecard
            testid="hero-diligence"
            label="Diligence Score"
            value={data.hero.diligence?.score != null ? Math.round(data.hero.diligence.score) : "—"}
            sub={data.hero.diligence?.grade ? `Grade ${data.hero.diligence.grade}` : "Wave-6 composite"}
            tone={
              data.hero.diligence?.score >= 80 ? "emerald"
              : data.hero.diligence?.score >= 60 ? "amber" : "rose"
            }
            icon={Award}
            link="/banking/diligence"
          />
          <HeroScorecard
            testid="hero-compliance"
            label="Continuous Compliance"
            value={pct(data.hero.compliance_avg_pct)}
            sub="CIP/CDD avg coverage"
            tone={data.hero.compliance_avg_pct >= 80 ? "emerald" : data.hero.compliance_avg_pct >= 50 ? "amber" : "rose"}
            icon={FileCheck2}
            link="/banking/continuous-compliance"
          />
          <HeroScorecard
            testid="hero-sar"
            label="SAR pipeline"
            value={fmt(data.hero.sar_funnel.open_alerts)}
            sub={`${fmt(data.hero.sar_funnel.sar_filed)} SARs filed`}
            tone={data.hero.sar_funnel.open_alerts > 0 ? "amber" : "emerald"}
            icon={ScrollText}
            link="/banking/continuous-compliance"
          />
          <HeroScorecard
            testid="hero-kill-switch"
            label="Kill switch"
            value={data.hero.kill_switch_level}
            sub="5 levels available"
            tone={data.hero.kill_switch_level === "normal" ? "emerald" : "amber"}
            icon={Zap}
            link="/banking/security-posture"
          />
          <HeroScorecard
            testid="hero-integrations"
            label="Integrations"
            value={`${data.hero.integrations.live} live`}
            sub={`${data.hero.integrations.mock} mock · ${data.hero.integrations.broken} broken`}
            tone={data.hero.integrations.broken > 0 ? "rose" : data.hero.integrations.mock > 0 ? "amber" : "emerald"}
            icon={Network}
            link="/banking/trust"
          />
        </div>
      </div>

      {/* NIST CSF — 5 functions */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <Layers className="w-4 h-4 text-slate-400" /> NIST CSF · Control Matrix
          </h3>
          <div className="text-[10.5px] text-slate-500 tabular-nums">
            {data.control_totals.operational}/{data.control_totals.total} controls operational
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {["identify", "protect", "detect", "respond", "recover"].map(k => (
            <CsfFunctionCard key={k} fnKey={k} body={data.nist_csf[k]} />
          ))}
        </div>
      </div>

      {/* Framework attestation strip */}
      <div>
        <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Award className="w-4 h-4 text-slate-400" /> Compliance Framework Attestation
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="frameworks-strip">
          {data.frameworks.map(f => <FrameworkPill key={f.key} framework={f} />)}
        </div>
      </div>

      {/* Footer disclaimer */}
      <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-2.5 text-[11px] text-slate-500" data-testid="trust-disclaimer">
        {data._disclaimer}
      </div>
    </div>
  );
}
